import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Loader2, AlertCircle, MessageCircle } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supportService } from '../../services/supportService';
import { TICKET_STATUS_BADGE } from '../../types/support';

function NewTicketModal({ onClose }: { onClose: () => void }) {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => supportService.createTicket(subject.trim(), body.trim()),
    onSuccess: (ticket) => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] });
      navigate(`/support/tickets/${ticket.id}`);
    },
    onError: (err: Error) => setError(err.message),
  });

  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
      <div className="bg-slate-800 rounded-xl border border-slate-700 w-full max-w-lg">
        <div className="p-5 border-b border-slate-700">
          <h2 className="text-white font-semibold">New Support Ticket</h2>
        </div>
        <div className="p-5 space-y-4">
          {error && (
            <div className="flex items-center gap-2 text-red-400 bg-red-900/20 p-3 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}
          <div>
            <label className="block text-sm text-slate-400 mb-1">Subject</label>
            <input
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500"
              maxLength={255}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder="Brief description of your issue"
            />
          </div>
          <div>
            <label className="block text-sm text-slate-400 mb-1">Description</label>
            <textarea
              className="w-full bg-slate-700 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:ring-1 focus:ring-teal-500 resize-none"
              rows={5}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              placeholder="Describe your issue in detail"
            />
          </div>
        </div>
        <div className="p-5 border-t border-slate-700 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-sm text-slate-300 hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={() => mutation.mutate()}
            disabled={!subject.trim() || !body.trim() || mutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 disabled:opacity-50 text-white rounded-lg text-sm transition-colors"
          >
            {mutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            Submit Ticket
          </button>
        </div>
      </div>
    </div>
  );
}

export default function SupportTickets() {
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const { data: tickets, isLoading, error } = useQuery({
    queryKey: ['support-tickets'],
    queryFn: () => supportService.listTickets(),
  });

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <MessageCircle className="w-6 h-6 text-teal-400" />
          <h1 className="text-xl font-semibold text-white">Support Tickets</h1>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center gap-2 px-4 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg text-sm transition-colors"
        >
          <Plus className="w-4 h-4" />
          New Ticket
        </button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-teal-400 animate-spin" />
        </div>
      )}

      {error && (
        <div className="flex items-center gap-2 text-red-400 bg-red-900/20 p-4 rounded-lg">
          <AlertCircle className="w-4 h-4" />
          Failed to load tickets
        </div>
      )}

      {tickets && tickets.length === 0 && (
        <div className="text-center py-12 text-slate-500">
          <MessageCircle className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No tickets yet. Submit one if you need help.</p>
        </div>
      )}

      {tickets && tickets.length > 0 && (
        <div className="space-y-2">
          {tickets.map((ticket) => (
            <div
              key={ticket.id}
              onClick={() => navigate(`/support/tickets/${ticket.id}`)}
              className="bg-slate-800 border border-slate-700 rounded-lg p-4 cursor-pointer hover:border-slate-600 transition-colors"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm font-medium truncate">{ticket.subject}</p>
                  <p className="text-slate-500 text-xs mt-1">
                    Updated {new Date(ticket.updated_at).toLocaleString()}
                  </p>
                </div>
                <span
                  className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full ${TICKET_STATUS_BADGE[ticket.status] ?? TICKET_STATUS_BADGE.open}`}
                >
                  {ticket.status.replace('_', ' ')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && <NewTicketModal onClose={() => setShowModal(false)} />}
    </div>
  );
}
